// -----------------------------------------------------------------------------
//  LISTINI PREDEFINITI — un posto solo, usato sia alla creazione del partner
//  sia quando si aggiunge un listino a un partner che esiste già.
//
//  Le righe hanno la forma della BandTable (`min_km`, `max_km`, `mode`,
//  `price`, `price_per_km`, e le colonne in più dove servono), non quella del
//  backend: la traduzione la fa `bandPricing` al salvataggio.
//
//  ⚠️ I confini sono [min, max): la fascia che a listino si legge «51–75 km»
//  qui è 50–75. Scritte 0–30 e 31–50, le distanze fra 30 e 31 km resterebbero
//  scoperte e il preventivo non troverebbe nessuna fascia.
//
//  ⚠️ La QUOTA PARTNER nasce uguale alla tariffa Simba: il partner parte da
//  quella e può alzarla, e la differenza è il suo ricarico. Nasce compilata
//  perché un listino partner vuoto fa quotare 0 le sue corse.
// -----------------------------------------------------------------------------

// Spostamenti interni: due colonne, 1 e 2 operatori. Le prime due fasce sono a
// prezzo pieno per corsa, dalla terza si paga al km.
const FLEET = [
  { min_km: 0, max_km: 30, mode: 'flat', op1: 20, op2: 45 },
  { min_km: 30, max_km: 50, mode: 'flat', op1: 27.5, op2: 65 },
  { min_km: 50, max_km: 75, mode: 'per_km', op1: 0.58, op2: 1.26 },
  { min_km: 75, max_km: 100, mode: 'per_km', op1: 0.52, op2: 1.14 },
  { min_km: 100, max_km: 130, mode: 'per_km', op1: 0.47, op2: 1.03 },
  { min_km: 130, max_km: 160, mode: 'per_km', op1: 0.43, op2: 0.94 },
  { min_km: 160, max_km: 195, mode: 'per_km', op1: 0.39, op2: 0.86 },
  { min_km: 195, max_km: 250, mode: 'per_km', op1: 0.36, op2: 0.8 },
  { min_km: 250, max_km: 300, mode: 'per_km', op1: 0.34, op2: 0.76 },
  { min_km: 300, max_km: 350, mode: 'per_km', op1: 0.33, op2: 0.73 },
  { min_km: 350, max_km: 400, mode: 'per_km', op1: 0.33, op2: 0.72 },
  { min_km: 400, max_km: 450, mode: 'per_km', op1: 0.32, op2: 0.71 },
  { min_km: 450, max_km: 500, mode: 'per_km', op1: 0.31, op2: 0.7 },
  { min_km: 500, max_km: 550, mode: 'per_km', op1: 0.31, op2: 0.69 },
  { min_km: 550, max_km: 600, mode: 'per_km', op1: 0.31, op2: 0.68 },
  { min_km: 600, max_km: null, mode: 'per_km', op1: 0.3, op2: 0.67 },
];

// Notturno: prezzo pieno fino a 30 km, poi fisso più €/km oltre la soglia.
const NOTTURNO = [
  { min_km: 0, max_km: 10, mode: 'flat', price: 50 },
  { min_km: 10, max_km: 20, mode: 'flat', price: 65 },
  { min_km: 20, max_km: 30, mode: 'flat', price: 80 },
  { min_km: 30, max_km: null, mode: 'flat_plus_km_excess', price: 80, price_per_km: 1.5 },
];

/** Una riga a colonna singola, con la quota partner uguale alla tariffa Simba. */
const singola = (b, prezzo) => ({
  min_km: b.min_km,
  max_km: b.max_km,
  mode: b.mode,
  price: prezzo,
  price_per_km: b.price_per_km ?? null,
  price_share: prezzo,
  price_share_per_km: b.price_per_km ?? null,
});

/**
 * Le fasce predefinite di un servizio, o `[]` se non ne ha.
 *
 * - `journey`: due colonne, 1 e 2 operatori;
 * - `pickup`: la colonna dei DUE operatori (è il costo di un pickup);
 * - `pickup_loaner`: la colonna di UN operatore (l'auto di cortesia la riporta
 *   una persona sola);
 * - `restaurant` e `restaurant_night`: il listino del notturno;
 * - `escort`: nessun predefinito concordato, si compila a mano.
 */
export function defaultBandsFor(service) {
  if (service === 'journey') {
    return FLEET.map((b) => ({
      min_km: b.min_km,
      max_km: b.max_km,
      mode: b.mode,
      price: b.op1,
      price2: b.op2,
      price_per_km: null,
      price2_per_km: null,
      // Quota partner: stessa cifra, su tutte e due le colonne. Il backend
      // cerca la quota col numero di operatori della corsa, quindi servono
      // entrambe o nessuna.
      price_share: b.op1,
      price_share2: b.op2,
    }));
  }
  if (service === 'pickup') return FLEET.map((b) => singola(b, b.op2));
  if (service === 'pickup_loaner') return FLEET.map((b) => singola(b, b.op1));
  if (service === 'restaurant' || service === 'restaurant_night') {
    return NOTTURNO.map((b) => singola(b, b.price));
  }
  return [];
}

/** Il listino valet: i km oltre il pacchetto si pagano 0,80 € l'uno. */
export const VALET_EXCESS_PER_KM = 0.8;

export default { defaultBandsFor, VALET_EXCESS_PER_KM };
